import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { QuestionCategoryComponent } from '@internal-components/administration-components/question-category-components/question-category-component/question-category.component';
import { BeegoAppointmentFormFactory } from '@internal-factories/forms/beego-appointment-form.factory';
import { IBeegoAppointment, ICreateBeegoAppointment } from '@internal-models/beego-appointment.model';
import { IQuestionCategory } from '@internal-models/question-category.model';
import { BeegoAppointmentProvider } from '@internal-providers/beego-appointment.provider';
import { PrintingProvider } from '@internal-providers/printing.provider';
import { QuestionCategoryProvider } from '@internal-providers/question-category.provider';
import { InternalNavigationType } from '@shared-directives/navigate.directive';
import {
	Action,
	DigibankObjective,
	ErrorType,
	NavigationDataKey,
	NavigationType,
	UserPermissions,
} from '@shared-libs/enums';
import { formIsValid, translate } from '@shared-libs/helpers';
import { IFormGroup } from '@shared-libs/interfaces';
import { NavigationDataManager } from '@shared-managers/navigation-data.manager';
import { IAddress } from '@shared-models/address.model';
import { ICalendarEvent } from '@shared-models/calendar-event.model';
import { ErrorService } from '@shared-services/error.service';
import { NavigationService } from '@shared-services/navigation.service';
import { PrintingService } from '@shared-services/print.service';
import { ValidationService } from '@shared-services/validation.service';
import { addHours, formatDuration } from 'date-fns';
import isPast from 'date-fns/esm/isPast';
import { intervalToDuration } from 'date-fns/fp';
import { nlBE } from 'date-fns/locale';
import { remove } from 'lodash';
import { interval } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';

@Component({
	selector: 'create-beego-appointment-component',
	templateUrl: 'create-beego-appointment.component.html',
	styleUrls: ['./create-beego-appointment.component.scss'],
})
export class CreateBeegoAppointmentComponent implements OnInit, OnDestroy {
	public calendarEvent: ICalendarEvent;
	public address: IAddress;
	public appointmentForm: IFormGroup<ICreateBeegoAppointment>;
	public questionCategories: Array<IQuestionCategory>;
	public showConfirmation: boolean = false;
	public beegoAppointment: IBeegoAppointment;
	public communication: Array<{ control: FormControl; label: string }>;
	public digibankObjectives: Array<{ value: DigibankObjective; label: string }>;
	public appointmentBufferEnd: Date;
	public calendarEventReservationEnd: Date;
	public countDown: string;
	public calendarEventNoLongerReserved: boolean = false;
	private subscription: Subscription;

	public readonly InternalNavigationType: typeof InternalNavigationType = InternalNavigationType;
	public readonly NavigationDataKey: typeof NavigationDataKey = NavigationDataKey;
	public readonly UserPermissions: typeof UserPermissions = UserPermissions;

	constructor(
		private readonly navigationDataManager: NavigationDataManager,
		private readonly navigationService: NavigationService,
		private readonly provider: BeegoAppointmentProvider,
		private readonly questionCategoryProvider: QuestionCategoryProvider,
		private readonly printingProvider: PrintingProvider,
		private readonly printingService: PrintingService,
		private readonly errorService: ErrorService,
		private readonly validationService: ValidationService
	) {}

	public async ngOnInit(): Promise<void> {
		void this.getQuestionCategories();
		this.calendarEvent = this.navigationDataManager.getData<ICalendarEvent>(NavigationDataKey.calendarEvent);
		this.address = this.navigationDataManager.getData<IAddress>(NavigationDataKey.address);
		this.calendarEventReservationEnd = this.navigationDataManager.getData<Date>(
			NavigationDataKey.calendarEventReservationEnd
		);
		const state = this.navigationDataManager.getData<IFormGroup<ICreateBeegoAppointment>>(
			NavigationDataKey.appointmentForm
		);
		if (state) {
			this.appointmentForm = state;
		} else {
			this.appointmentForm = BeegoAppointmentFormFactory.create(this.calendarEvent, this.address);
		}
		this.appointmentBufferEnd = addHours(new Date(this.calendarEvent.startsAt), 2);
		this.checkForConflictingZipcodes(this.appointmentForm);
		this.getCommunicationItems();
		this.getDigibankObjectives();
		this.subscription = interval(1000).subscribe(() => {
			this.getTimeDifference();
		});
	}

	public async ngOnDestroy(): Promise<void> {
		this.subscription.unsubscribe();
	}

	public close(): void {
		this.navigationService.navigateBack({ action: Action.CREATE });
	}

	public async print(): Promise<void> {
		const html = await this.printingProvider.getHTMLConfirmationForBeegoAppointment(
			this.beegoAppointment.AppointmentId
		);
		await this.printingService.print(html);
	}

	public async onAddDiscountCode(): Promise<void> {
		const discountCodes: Array<any> = this.appointmentForm.get('Digicheques').value;
		const discountCode = await this.provider.getDiscountCode(
			discountCodes.map((discountCode) => discountCode.code)
		);
		this.appointmentForm.get('Digicheques').setValue([...discountCodes, discountCode]);
	}

	public onRemoveDiscountCode(discountCode: any): void {
		const discountCodes: Array<any> = this.appointmentForm.get('Digicheques').value;
		remove(discountCodes, (code) => code === discountCode);
		this.appointmentForm.get('Digicheques').setValue(discountCodes);
	}

	public async createAppointment(): Promise<void> {
		if (formIsValid(this.appointmentForm, CreateBeegoAppointmentComponent)) {
			if (
				this.appointmentForm.value.Digicheques?.length === 0 &&
				!(await this.validationService.actionIsConfirmed('APPOINTMENT_WITHOUT_DIGICHEQUE'))
			) {
				return;
			}

			const appointment = BeegoAppointmentFormFactory.getValue(this.appointmentForm);
			this.beegoAppointment = await this.provider.create(appointment);
			this.showConfirmation = true;
		}
	}

	public async createQuestionCategory(): Promise<void> {
		void this.navigationService
			.navigateTo<{ questionCategory: IQuestionCategory }>(NavigationType.modal, QuestionCategoryComponent, {
				action: Action.CREATE,
			})
			.then((result) => {
				if (result?.questionCategory) {
					this.questionCategories.push(result.questionCategory);
					this.addQuestionCategoryToForm(result.questionCategory.id);
				}
				this.navigationDataManager.addData(NavigationDataKey.appointmentForm, this.appointmentForm);
			})
			.catch();
	}

	/**
	 * A check to make sure the zipcode for which the student is selected,
	 * is not adjusted to another zipcode.
	 * @param form The form including the address
	 */
	private checkForConflictingZipcodes(form: IFormGroup<ICreateBeegoAppointment>): void {
		let previousAddress: IAddress = form.get('Client.Address').value;
		form.get('Client.Address.zipcode').valueChanges.subscribe((zipcode: number) => {
			if (previousAddress.zipcode && previousAddress.zipcode !== zipcode) {
				form.get('Client.Address').patchValue(previousAddress, { emitEvent: false });
				this.errorService.showApplicationError(
					translate('BEEGO_APPOINTMENT.ZIPCODE_MISMATCH'),
					ErrorType.Component
				);
				form.get('Client').reset({ Address: previousAddress });
			} else {
				previousAddress = form.get('Client.Address').value;
			}
		});
	}

	private addQuestionCategoryToForm(questionCategoryId: string): void {
		const currentCategories = this.appointmentForm.get('Attendance.QuestionCategories').value;
		this.appointmentForm.get('Attendance.QuestionCategories').setValue([...currentCategories, questionCategoryId]);
	}

	private async getQuestionCategories(): Promise<void> {
		this.questionCategories = await this.questionCategoryProvider.getAll();
	}

	private getCommunicationItems(): void {
		this.communication = Object.keys((this.appointmentForm.get('Communication') as FormGroup).controls).map(
			(key) => ({
				control: (this.appointmentForm.get('Communication') as FormGroup).controls[key] as FormControl,
				label: translate(`COMMUNICATION.BEEGO.${key.toUpperCase()}`, { email: '' }),
			})
		);
	}

	/**
	 * Get the getDigibankObjectives options
	 * formatted for the form-select
	 */
	private getDigibankObjectives(): void {
		this.digibankObjectives = Object.keys(DigibankObjective)
			.filter((key) => ![DigibankObjective.Loan, DigibankObjective.None].includes(DigibankObjective[key]))
			.map((key) => ({
				value: DigibankObjective[key],
				label: translate(`EVENT.DIGIBANK_OBJECTIVE.${key.toUpperCase()}`),
			}));
	}

	private getTimeDifference() {
		let duration = intervalToDuration({
			start: new Date(),
			end: this.calendarEventReservationEnd,
		});
		this.countDown = formatDuration(duration, {
			delimiter: ', ',
			locale: nlBE,
		});

		if (isPast(this.calendarEventReservationEnd)) {
			this.calendarEventNoLongerReserved = true;
			this.subscription.unsubscribe();
		}
	}
}
